<template>
  <UIDrawer
    :is-open
    :z-index
    :title="t('More')"
    direction="to-top"
    size="md"
    with-back
    flat
    class="main-mobile-footer-more"
    @close="handleClose"
  >
    <template #title>
      <MoreSearch v-if="!isEmptyApp" />
    </template>
    <template v-if="!isEmptyApp">
      <MoreSection
        v-for="section in navSections"
        :key="section.label"
        :caption="section.label"
        :badge="section.badge"
      >
        <template #caption="{ caption }">
          <AddViewButton v-if="caption === DASHBOARD_NAME">
            <template #default="{ icon, disabled, label, onClick }">
              <UIButton
                :leading="icon"
                :disabled
                :label
                size="xs"
                variant="secondary"
                :hide-label-to-sm="false"
                @click="(onClick(), handleClose())"
              />
            </template>
          </AddViewButton>
        </template>
        <MoreSectionNavigation
          :disabled="section.label === DASHBOARD_NAME && isEditMode"
          :parent="section.label"
          @click="handleClose"
        />
      </MoreSection>
    </template>
    <MoreSection
      caption="Profile & settings"
      :items="settingsItems"
      @click="handleClose"
    />
  </UIDrawer>
</template>

<script setup lang="ts">
import { computed, markRaw, watch } from 'vue'

import { ModalClass } from '@types'

import { DASHBOARD_NAME, ROUTE_NAME } from '@/const'
import { SettingTabs } from '@/modules/modals/Settings/utils/enums'

import { useLocale } from '@/plugins/i18n'

import { useDashboardsStore } from '@/store/dashboards'
import { useModalsStore } from '@/store/modals'
import { useNavigationStore } from '@/store/navigation'

import AddViewButton from '@/views/Dashboards/components/AddViewButton.vue'
import MoreSearch from './MoreSearch.vue'
import ModalsSettings from '@/modules/modals/Settings/Settings.vue'
import MoreSection from './components/MoreSection.vue'
import MoreSectionNavigation from './components/MoreSectionNavigation.vue'
import {
  Cog6ToothIcon,
  TicketIcon,
  UserCircleIcon,
} from '@heroicons/vue/24/outline'
import { UIButton } from '@ui/buttons'
import { UIDrawer } from '@ui/modals'

type Props = {
  modal: ModalClass<any>
  isEmptyApp?: boolean
}

const props = defineProps<Props>()

const { t } = useLocale('layouts.Main')

const dashboardsStore = useDashboardsStore()
const modalsStore = useModalsStore()
const navigationStore = useNavigationStore()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const navSections = computed(() => navigationStore.getParentNavigation)

const settingsItems = computed(() => [
  ...Object.values(SettingTabs).map(tab => ({
    icon: tab === SettingTabs.ACCOUNT ? UserCircleIcon : Cog6ToothIcon,
    label: tab,
    action: () => {
      const modalInstance = modalsStore.init(
        'settings',
        markRaw(ModalsSettings),
      )
      modalInstance?.open(modalsStore.getZIndex(), {
        tab,
      })
    },
  })),
  {
    icon: TicketIcon,
    label: 'Subscription',
    route: ROUTE_NAME.SUBSCRIPTION,
  },
])

const isEditMode = computed(() => dashboardsStore.getEditMode)

const handleClose = () => {
  props.modal.close()
}

watch(isEditMode, value => {
  if (!value) return
  handleClose()
})
</script>

<style>
.main-mobile-footer-more {
  .ui-drawer-body {
    @apply pb-safe-offset-4;
    @apply overflow-y-auto;
  }
}
</style>
